import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePlayerAuth } from '@/contexts/PlayerAuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FONT_B, FONT_H, GRADIENT_BG, SIGNUP_PATH } from './theme';

export const MARKETING_LINKS = [
  { label: 'Pool Types', to: '/pool-types' },
  { label: 'Golf Societies', to: '/golf-societies' },
  { label: 'Sports Clubs', to: '/sports-clubs' },
  { label: 'Bars & Pubs', to: '/bars-and-pubs' },
  { label: 'Guides', to: '/guides/run-a-sweepstake' },
  { label: 'FAQ', to: '/faq' },
];

const AVATAR_COLORS = ['#E91E8C', '#6C3CE0', '#FF6B35', '#00C2A8', '#3498DB', '#9B59B6'];
function avatarColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return 'U';
  const p = name.trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[p.length - 1][0] : p[0][0]).toUpperCase();
}

function AccountMenu({ testIdPrefix }) {
  const navigate = useNavigate();
  const { user: adminUser, logout: adminLogout } = useAuth();
  const { player, logout: playerLogout } = usePlayerAuth();
  const name = adminUser?.name || player?.display_name || player?.name
    || (player ? `${player.first_name || ''} ${player.last_name || ''}`.trim() : '') || '';
  const email = adminUser?.email || player?.email || '';
  const dashPath = adminUser ? '/admin/dash' : '/player/dash';
  const logout = adminUser ? adminLogout : playerLogout;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold hover:opacity-90 transition-opacity ring-2 ring-white/60"
          style={{ backgroundColor: avatarColor(name), ...FONT_H }}
          data-testid={`${testIdPrefix}-avatar`}
        >
          {initials(name)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-semibold truncate">{name || 'Signed in'}</p>
          {email && <p className="text-xs text-gray-500 truncate">{email}</p>}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate(dashPath)} className="cursor-pointer" data-testid={`${testIdPrefix}-go-dashboard`}>
          <LayoutDashboard className="w-4 h-4 mr-2" /> My dashboard
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="cursor-pointer text-red-600" data-testid={`${testIdPrefix}-logout`}>
          <LogOut className="w-4 h-4 mr-2" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MarketingNav({ overDark = false }) {
  const { pathname } = useLocation();
  const { user: adminUser } = useAuth();
  const { player } = usePlayerAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(typeof window !== 'undefined' && window.scrollY > 8);
  const isLoggedIn = !!adminUser || !!player;

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Transparent over the hero; solid once the user scrolls.
  const solid = scrolled || open;
  const onDark = overDark && !solid;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${solid ? 'bg-white/[0.88] backdrop-blur-xl border-b border-gray-100' : 'bg-transparent border-b border-transparent'}`}
      data-testid="marketing-nav"
      data-scrolled={solid ? 'true' : 'false'}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex-shrink-0" data-testid="marketing-nav-logo">
          <img src={onDark ? '/brand/landscape-logo-white.png' : '/brand/landscape-logo.png'} alt="SportSweeps" className="h-8 md:h-9 w-auto block" />
        </Link>

        <div className="hidden lg:flex items-center gap-5 xl:gap-7 text-sm font-semibold" style={FONT_B}>
          {MARKETING_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`transition-colors ${onDark
                ? (pathname === l.to ? 'text-white' : 'text-white/75 hover:text-white')
                : (pathname === l.to ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900')}`}
              data-testid={`marketing-nav-${l.to.replace(/\//g, '-').replace(/^-/, '')}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {isLoggedIn ? <AccountMenu testIdPrefix="marketing-nav" /> : (
            <>
              <Link
                to="/login"
                className={`text-sm font-bold px-5 py-2 rounded-full border transition-colors ${onDark ? 'border-white/30 text-white hover:bg-white/10' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                style={FONT_B}
                data-testid="marketing-nav-login"
              >
                Log In
              </Link>
              <Link
                to={SIGNUP_PATH}
                className="text-sm font-bold px-5 py-2.5 rounded-full text-white hover:opacity-90 transition-opacity"
                style={{ ...FONT_B, background: GRADIENT_BG }}
                data-testid="marketing-nav-get-started"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        <div className="flex lg:hidden items-center gap-2">
          {isLoggedIn ? <AccountMenu testIdPrefix="marketing-nav-mobile" /> : (
            <Link
              to="/login"
              className="text-xs font-bold px-4 py-2 rounded-full text-white"
              style={{ ...FONT_B, background: GRADIENT_BG }}
              data-testid="marketing-nav-mobile-login"
            >
              Log In
            </Link>
          )}
          <button
            onClick={() => setOpen(!open)}
            className={`p-2 transition-colors ${onDark ? 'text-white hover:text-white/80' : 'text-gray-600 hover:text-gray-900'}`}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="marketing-nav-mobile-menu"
            data-testid="marketing-nav-mobile-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div id="marketing-nav-mobile-menu" className="lg:hidden border-t border-gray-100 bg-white px-4 py-3" data-testid="marketing-nav-mobile-menu">
          <div className="flex flex-col">
            {MARKETING_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 text-sm font-semibold text-gray-700 border-b border-gray-50 last:border-0"
                style={FONT_B}
                data-testid={`marketing-nav-mobile-link${l.to.replace(/\//g, '-')}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to={SIGNUP_PATH}
              className="mt-3 w-full text-center rounded-full py-3 text-sm font-bold text-white"
              style={{ ...FONT_B, background: GRADIENT_BG }}
              data-testid="marketing-nav-mobile-cta"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
